import { indexedDBService, type ProcessedData } from "@/services/storage";
import { sourceDataLoader } from "@/services/data";
import type { DataflowData } from "@/types/stores/dataflow";
import type { DataProcessingStore } from "@/types/stores/data-processing";
import { logger } from "@/utils/debug";
import { create } from "zustand";
import { devtools } from "zustand/middleware";

const initialState = {
  processedData: {},
  originalData: {},
  isProcessing: {},
  processingError: {},
};

export const useDataProcessingStore = create<DataProcessingStore>()(
  devtools(
    (set, get) => ({
      ...initialState,

      /**
       * Loads source data for a dataflow and preserves geocoded coordinates.
       *
       * Transformers are visual-only and do not affect data processing.
       * This function loads source data and preserves any geocoded latitude/longitude
       * columns that may have been added via the geocoding enhancement feature.
       */
      executeDataflow: async (dataflowId: string, dataflow: DataflowData) => {
        // Check if already processing
        if (get().isProcessing[dataflowId]) return;

        set(
          (state) => ({
            isProcessing: { ...state.isProcessing, [dataflowId]: true },
            processingError: { ...state.processingError, [dataflowId]: null },
          }),
          false,
          "data-processing/executeDataflow-start"
        );

        try {
          // FIRST: Try to load existing processed data from IndexedDB to preserve geocoded data
          let existingProcessedDataFromStorage: ProcessedData | null = null;
          try {
            existingProcessedDataFromStorage = await indexedDBService.getTransformedData(dataflowId);
            if (existingProcessedDataFromStorage) {
              logger.log(`[DataProcessing] Loaded existing processed data from IndexedDB for ${dataflowId} (${existingProcessedDataFromStorage.data.length} rows)`);
            }
          } catch (error) {
            logger.warn(`[DataProcessing] Could not load existing processed data from IndexedDB for ${dataflowId}:`, error);
          }

          const transformers = dataflow.flow_definition_json || [];

          // Find the data source transformer
          const inputTransformer = transformers.find(
            (t) => t.type === "data_input" || t.type === "user_file"
          );

          if (!inputTransformer) {
            throw new Error("No input transformer found");
          }

          // Load source data
          let sourceData: any[] = [];
          let sourceColumns: string[] | undefined;

          if (inputTransformer.type === "user_file") {
            sourceData = await sourceDataLoader.loadUserFileData(
              inputTransformer
            );
            sourceColumns = undefined;
            
            if (sourceData && sourceData.length > 0) {
              const firstRow = sourceData[0];
              const hasLat = firstRow.hasOwnProperty("latitude") || 
                           Object.keys(firstRow).some((k) => /^latitude$/i.test(k));
              const hasLon = firstRow.hasOwnProperty("longitude") || 
                           Object.keys(firstRow).some((k) => /^longitude$/i.test(k));
              if (hasLat || hasLon) {
                logger.log(`[DataProcessing] File data (${inputTransformer.file_id}) already contains geocoded coordinates`);
              }
            }
          } else if (inputTransformer.type === "data_input") {
            sourceData = await sourceDataLoader.loadDatasetData(
              inputTransformer
            );
            sourceColumns = undefined;
            
            if (sourceData && sourceData.length > 0) {
              const firstRow = sourceData[0];
              const hasLat = firstRow.hasOwnProperty("latitude") || 
                           Object.keys(firstRow).some((k) => /^latitude$/i.test(k));
              const hasLon = firstRow.hasOwnProperty("longitude") || 
                           Object.keys(firstRow).some((k) => /^longitude$/i.test(k));
              if (hasLat || hasLon) {
                logger.log(`[DataProcessing] Dataset data (${inputTransformer.db_table_id}) already contains geocoded coordinates`);
              }
            }
          }

          // Ensure we have data before proceeding
          if (!sourceData || sourceData.length === 0) {
            throw new Error("No source data available");
          }

          const originalDataCopy = structuredClone(sourceData);

          let currentData = [...sourceData];
          let currentColumns =
            sourceColumns || Object.keys(currentData[0] || {});

          const existingProcessedData = existingProcessedDataFromStorage || get().processedData[dataflowId];
          const geocodedDataMap = new Map<
            string,
            { lat: number | null; lon: number | null }
          >();
          let preservedLatColumn: string | null = null;
          let preservedLonColumn: string | null = null;

          if (
            existingProcessedData?.data &&
            existingProcessedData.data.length > 0
          ) {
            // Check if latitude/longitude columns exist
            const existingColumns =
              existingProcessedData.metadata.columns || [];
            const latColumn = existingColumns.find((col: string) =>
              /^latitude$/i.test(col)
            );
            const lonColumn = existingColumns.find((col: string) =>
              /^longitude$/i.test(col)
            );

            if (latColumn && lonColumn) {
              preservedLatColumn = latColumn;
              preservedLonColumn = lonColumn;
              logger.log(`[DataProcessing] Found geocoded columns: ${latColumn}, ${lonColumn}`);

              // Create a map of geocoded data using a row signature for matching
              // Try to match rows by creating a signature from all non-geo columns
              let geocodedCount = 0;
              existingProcessedData.data.forEach((row: any) => {
                // Create a signature from all columns except lat/lon
                const signatureColumns = existingColumns.filter(
                  (col: string) => col !== latColumn && col !== lonColumn
                );
                const signature = JSON.stringify(
                  signatureColumns.map((col: string) => row[col])
                );

                // Store geocoded values if they exist
                if (row[latColumn] != null && row[latColumn] !== "" && 
                    row[lonColumn] != null && row[lonColumn] !== "") {
                  geocodedDataMap.set(signature, {
                    lat: row[latColumn],
                    lon: row[lonColumn],
                  });
                  geocodedCount++;
                }
              });
              logger.log(`[DataProcessing] Preserving ${geocodedCount} geocoded coordinates`);
            }
          }

          // Restore geocoded columns if they existed
          if (
            preservedLatColumn &&
            preservedLonColumn &&
            geocodedDataMap.size > 0
          ) {
            // Add geocoded columns back to the data
            if (!currentColumns.includes(preservedLatColumn)) {
              currentColumns.push(preservedLatColumn);
            }
            if (!currentColumns.includes(preservedLonColumn)) {
              currentColumns.push(preservedLonColumn);
            }

            // Match rows and restore geocoded values
            let restoredCount = 0;
            currentData = currentData.map((row) => {
              // Create a signature from all non-geo columns for matching
              const signatureColumns = currentColumns.filter(
                (col) =>
                  col !== preservedLatColumn && col !== preservedLonColumn
              );
              const signature = JSON.stringify(
                signatureColumns.map((col) => row[col])
              );

              const geocoded = geocodedDataMap.get(signature);
              if (geocoded) {
                restoredCount++;
                return {
                  ...row,
                  [preservedLatColumn!]: geocoded.lat,
                  [preservedLonColumn!]: geocoded.lon,
                };
              }
              return row;
            });
            logger.log(`[DataProcessing] Restored ${restoredCount} geocoded coordinates to processed data`);
          }

          const result = {
            data: currentData,
            metadata: {
              rowCount: currentData.length,
              columnCount: currentColumns.length,
              columns: currentColumns,
            },
          };

          set(
            (state) => ({
              processedData: { ...state.processedData, [dataflowId]: result },
              originalData: {
                ...state.originalData,
                [dataflowId]: originalDataCopy,
              },
              isProcessing: { ...state.isProcessing, [dataflowId]: false },
              processingError: { ...state.processingError, [dataflowId]: null },
            }),
            false,
            "data-processing/executeDataflow-success"
          );

          // Store in IndexedDB for persistence (including preserved geocoded data)
          try {
            await indexedDBService.storeTransformedData(dataflowId, result);
            logger.log(`[DataProcessing] Successfully stored processed data (with geocoded coordinates if present) to IndexedDB for ${dataflowId}`);
            
            // Verify geocoded data was stored
            if (preservedLatColumn && preservedLonColumn) {
              const verifyData = await indexedDBService.getTransformedData(dataflowId);
              if (verifyData) {
                const hasLat = verifyData.metadata.columns.includes(preservedLatColumn);
                const hasLon = verifyData.metadata.columns.includes(preservedLonColumn);
                const hasGeocodedValues = verifyData.data.some((row: any) => 
                  (row[preservedLatColumn] != null && row[preservedLatColumn] !== "") ||
                  (row[preservedLonColumn] != null && row[preservedLonColumn] !== "")
                );
                logger.log(`[DataProcessing] Verification: lat column=${hasLat}, lon column=${hasLon}, has values=${hasGeocodedValues}`);
              }
            }
          } catch (error) {
            logger.error("[DataProcessing] Failed to store in IndexedDB:", error);
            // Don't fail the whole operation if IndexedDB fails
          }
        } catch (error) {
          const errorMsg =
            error instanceof Error ? error.message : "Unknown error";
          const errorObj = error instanceof Error ? error : new Error(errorMsg);

          set(
            (state) => ({
              isProcessing: { ...state.isProcessing, [dataflowId]: false },
              processingError: {
                ...state.processingError,
                [dataflowId]: errorObj,
              },
            }),
            false,
            "data-processing/executeDataflow-error"
          );
        }
      },

      /**
       * Updates processed data and stores it in IndexedDB for persistence.
       *
       * This function is used to update data after operations like geocoding,
       * which adds latitude/longitude columns. The data (including geocoded
       * lat/lng values) is stored in IndexedDB to persist across sessions.
       *
       * IMPORTANT: This function ensures geocoded coordinates are:
       * 1. Saved to IndexedDB for persistence across page reloads
       * 2. Updated in the in-memory store immediately
       * 3. Available to all components (map, table, etc.) via the store
       *
       * @param dataflowId - The dataflow ID
       * @param data - The processed data (including geocoded lat/lng if present)
       * @param columns - The column names (including latitude/longitude if geocoded)
       */
      updateProcessedData: async (
        dataflowId: string,
        data: any[],
        columns: string[]
      ) => {
        // Create a deep copy of the data to ensure it's properly serialized and saved
        // This prevents any mutations from affecting the stored data
        // Geocoded lat/lng values are included in this copy and will be persisted
        const dataDeepCopy = JSON.parse(JSON.stringify(data));

        const result: ProcessedData = {
          data: dataDeepCopy,
          metadata: {
            rowCount: dataDeepCopy.length,
            columnCount: columns.length,
            columns: [...columns], // Also copy the columns array
          },
        };

        // Update in memory store FIRST - this ensures components see the update immediately
        // Components that subscribe to processedData will automatically re-render
        set(
          (state) => ({
            processedData: { ...state.processedData, [dataflowId]: result },
          }),
          false,
          "data-processing/updateProcessedData"
        );

        // Store in IndexedDB for persistence (including geocoded lat/lng values)
        // This ensures data persists across browser sessions and page reloads
        try {
          await indexedDBService.storeTransformedData(dataflowId, result);
          logger.log(
            `[DataProcessing] Successfully stored processed data for ${dataflowId} in IndexedDB (${dataDeepCopy.length} rows, ${columns.length} columns)`
          );
        } catch (error) {
          logger.error(
            `[DataProcessing] Failed to store processed data in IndexedDB for ${dataflowId}:`,
            error
          );
          // Don't fail the whole operation if IndexedDB fails
          // The in-memory store has already been updated, so the UI will still work
        }
      },

      clearProcessedData: (dataflowId: string) => {
        set(
          (state) => {
            const { [dataflowId]: _, ...remainingData } = state.processedData;
            const { [dataflowId]: __, ...remainingOriginal } =
              state.originalData;
            const { [dataflowId]: ____, ...remainingProcessing } =
              state.isProcessing;
            const { [dataflowId]: _____, ...remainingError } =
              state.processingError;

            return {
              processedData: remainingData,
              originalData: remainingOriginal,
              isProcessing: remainingProcessing,
              processingError: remainingError,
            };
          },
          false,
          "data-processing/clearProcessedData"
        );

        indexedDBService.deleteTransformedData(dataflowId).catch((error: any) => {
          logger.error("Failed to clear from IndexedDB:", error);
        });
      },

      getProcessedData: (dataflowId: string): ProcessedData | null => {
        const state = get();
        return state.processedData[dataflowId] || null;
      },

      loadProcessedDataFromStorage: async (dataflowId: string) => {
        try {
          const storedData = await indexedDBService.getTransformedData(
            dataflowId
          );
          if (storedData) {
            const originalDataCopy = structuredClone(storedData.data);
            set(
              (state) => ({
                processedData: {
                  ...state.processedData,
                  [dataflowId]: storedData,
                },
                originalData: {
                  ...state.originalData,
                  [dataflowId]: originalDataCopy,
                },
              }),
              false,
              "data-processing/loadProcessedDataFromStorage"
            );
            return storedData;
          }
          return null;
        } catch (error) {
          logger.error(
            `Failed to load processed data from storage for ${dataflowId}:`,
            error
          );
          return null;
        }
      },

      getOriginalData: (dataflowId: string): any[] | null => {
        const state = get();
        return state.originalData[dataflowId] || null;
      },
    }),
    {
      name: "data-processing-store",
    }
  )
);

// Export hooks for convenience
export const useProcessedData = (dataflowId: string) =>
  useDataProcessingStore((state) => state.processedData[dataflowId]);

export const useIsProcessing = (dataflowId: string) =>
  useDataProcessingStore((state) => state.isProcessing[dataflowId] || false);

export const useProcessingError = (dataflowId: string) =>
  useDataProcessingStore((state) => state.processingError[dataflowId]);

export const useOriginalData = (dataflowId: string) =>
  useDataProcessingStore((state) => state.originalData[dataflowId]);

